import React, { useMemo, useState } from 'react'
import {
    Box,
    Flex,
    Icon,
    IconButton,
    Input,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Spinner,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react'
import { FaCircle, FaClockRotateLeft } from 'react-icons/fa6'

import { useClinic } from '~contexts/clinic-context'
import {
    SupportBotConversationFieldsFragment,
    useMyClinicAgentConversationsQuery,
} from '~graphql/generated/graphql'
import { useAskChckvetDock } from './ask-chckvet-context'

const CONVERSATION_LIMIT = 50

type SessionGroup = {
    label: string
    conversations: SupportBotConversationFieldsFragment[]
}

/**
 * Cursor-style thread switcher: search + recency groups so staff can keep one
 * thread per problem and jump back into it, instead of one endless chat.
 */
export default function AskChckvetSessionMenu() {
    const { clinic } = useClinic()
    const clinicId = clinic?.id
    const { activeConversationId, setActiveConversationId } =
        useAskChckvetDock()
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState('')

    const { data, loading } = useMyClinicAgentConversationsQuery({
        variables: { clinicId: clinicId ?? '', limit: CONVERSATION_LIMIT },
        skip: !clinicId || !isOpen,
        fetchPolicy: 'cache-and-network',
    })

    const conversations = data?.myClinicAgentConversations ?? []

    const groups = useMemo(
        () => groupConversationsByRecency(conversations, query),
        [conversations, query],
    )

    const hasResults = groups.some(group => group.conversations.length > 0)

    return (
        <Popover
            isOpen={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => {
                setIsOpen(false)
                setQuery('')
            }}
            placement="bottom-end"
            isLazy
        >
            <PopoverTrigger>
                <IconButton
                    aria-label="Your chats"
                    title="Your chats"
                    icon={<Icon as={FaClockRotateLeft} />}
                    size="sm"
                    variant="ghost"
                />
            </PopoverTrigger>
            <Portal>
                <PopoverContent w="340px" shadow="lg" rounded="xl">
                    <PopoverBody p={2}>
                        <Input
                            value={query}
                            onChange={event => setQuery(event.target.value)}
                            placeholder="Search chats…"
                            size="sm"
                            rounded="lg"
                            mb={2}
                            autoFocus
                        />
                        <Box maxH="320px" overflowY="auto">
                            {loading && conversations.length === 0 && (
                                <Flex
                                    align="center"
                                    gap={2}
                                    color="gray.500"
                                    px={2}
                                    py={3}
                                >
                                    <Spinner size="xs" />
                                    <Text fontSize="sm">Loading chats…</Text>
                                </Flex>
                            )}
                            {!loading && !hasResults && (
                                <Text
                                    fontSize="sm"
                                    color="gray.500"
                                    px={2}
                                    py={3}
                                >
                                    {query
                                        ? 'No chats match that search.'
                                        : 'No chats yet. Ask a question to start one.'}
                                </Text>
                            )}
                            {groups.map(group =>
                                group.conversations.length === 0 ? null : (
                                    <Box key={group.label} mb={2}>
                                        <Text
                                            fontSize="xs"
                                            fontWeight="bold"
                                            color="gray.500"
                                            textTransform="uppercase"
                                            letterSpacing="wide"
                                            px={2}
                                            py={1}
                                        >
                                            {group.label}
                                        </Text>
                                        <VStack align="stretch" spacing={0}>
                                            {group.conversations.map(
                                                conversation => (
                                                    <SessionRow
                                                        key={conversation.id}
                                                        conversation={
                                                            conversation
                                                        }
                                                        isActive={
                                                            conversation.id ===
                                                            activeConversationId
                                                        }
                                                        onSelect={() => {
                                                            setActiveConversationId(
                                                                conversation.id,
                                                            )
                                                            setIsOpen(false)
                                                            setQuery('')
                                                        }}
                                                    />
                                                ),
                                            )}
                                        </VStack>
                                    </Box>
                                ),
                            )}
                        </Box>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    )
}

function SessionRow({
    conversation,
    isActive,
    onSelect,
}: {
    conversation: SupportBotConversationFieldsFragment
    isActive: boolean
    onSelect: () => void
}) {
    return (
        <Box
            role="button"
            tabIndex={0}
            px={2}
            py={2}
            rounded="lg"
            bg={isActive ? 'brand.50' : 'transparent'}
            _hover={{ bg: isActive ? 'brand.50' : 'gray.50' }}
            onClick={onSelect}
            onKeyDown={event => {
                if (event.key === 'Enter') onSelect()
            }}
        >
            <Flex align="center" gap={2}>
                <Text
                    fontSize="sm"
                    noOfLines={1}
                    fontWeight={isActive ? 'semibold' : 'normal'}
                    flex={1}
                    minW={0}
                >
                    {conversation.title || 'Untitled chat'}
                </Text>
                {conversation.pendingRunId && (
                    <Tooltip label="Still working" openDelay={300}>
                        <Box>
                            <Icon
                                as={FaCircle}
                                boxSize={2}
                                color="brand.400"
                                flexShrink={0}
                            />
                        </Box>
                    </Tooltip>
                )}
            </Flex>
        </Box>
    )
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Newest-first recency buckets, filtered by title search. `updatedAt` is epoch
 * millis.
 */
export function groupConversationsByRecency(
    conversations: SupportBotConversationFieldsFragment[],
    query: string,
    now: number = Date.now(),
): SessionGroup[] {
    const normalizedQuery = query.trim().toLowerCase()
    const filtered = normalizedQuery
        ? conversations.filter(conversation =>
              (conversation.title ?? '')
                  .toLowerCase()
                  .includes(normalizedQuery),
          )
        : conversations

    const sorted = [...filtered].sort((a, b) => b.updatedAt - a.updatedAt)

    const startOfToday = new Date(now)
    startOfToday.setHours(0, 0, 0, 0)
    const todayStart = startOfToday.getTime()
    const yesterdayStart = todayStart - DAY_MS
    const weekStart = todayStart - 7 * DAY_MS

    const groups: SessionGroup[] = [
        { label: 'Today', conversations: [] },
        { label: 'Yesterday', conversations: [] },
        { label: 'Previous 7 days', conversations: [] },
        { label: 'Older', conversations: [] },
    ]

    for (const conversation of sorted) {
        if (conversation.updatedAt >= todayStart) {
            groups[0].conversations.push(conversation)
        } else if (conversation.updatedAt >= yesterdayStart) {
            groups[1].conversations.push(conversation)
        } else if (conversation.updatedAt >= weekStart) {
            groups[2].conversations.push(conversation)
        } else {
            groups[3].conversations.push(conversation)
        }
    }

    return groups
}
